import React, { useEffect, useMemo, useRef, useState } from "react";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import Sidebar from "../../../components/ui/Sidebar";
import Header from "../../../components/ui/Header";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

// ---------- time helpers ----------
const IST_TZ = "Asia/Kolkata";

function formatMinutes(min) {
  const abs = Math.abs(Number(min || 0));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatTimeFromISTString(ist) {
  if (!ist) return "-";
  const parts = String(ist).split(" ");
  return parts[1] ? `${parts[1]} ${parts[2] || ""}`.trim() : "-";
}

function formatDateFromISTString(ist) {
  if (!ist) return "-";
  const parts = String(ist).split(" ");
  return parts[0] || "-";
}

// ✅ Convert UTC date (ISO string or Date) -> IST date/time strings (no dayjs needed)
function utcToISTParts(utc) {
  if (!utc) return { date: "-", time: "-", full: "-" };
  const d = utc instanceof Date ? utc : new Date(utc);
  if (Number.isNaN(d.getTime())) return { date: "-", time: "-", full: "-" };

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // en-GB gives dd/mm/yyyy; we'll rearrange to yyyy-mm-dd
  const parts = formatter.formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;

  const dd = get("day");
  const mm = get("month");
  const yyyy = get("year");
  const hour = get("hour");
  const minute = get("minute");
  const dayPeriod = get("dayPeriod"); // AM/PM

  const date = yyyy && mm && dd ? `${yyyy}-${mm}-${dd}` : "-";
  const time = hour && minute ? `${hour}:${minute} ${dayPeriod || ""}`.trim() : "-";
  const full = `${date} ${time}`.trim();

  return { date, time, full };
}

function inr(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(v);
}

function badgeClass(status) {
  const s = String(status || "").toUpperCase();
  if (s.includes("COMPLET") || s.includes("CAPTURED")) return "bg-green-50 text-green-700 border-green-200";
  if (s.includes("PENDING") || s.includes("AUTH")) return "bg-yellow-50 text-yellow-700 border-yellow-200";
  if (s.includes("FAIL") || s.includes("CANCEL")) return "bg-red-50 text-red-700 border-red-200";
  if (s.includes("REFUND")) return "bg-indigo-50 text-indigo-700 border-indigo-200";
  return "bg-gray-50 text-gray-700 border-gray-200";
}

function safeStr(v) {
  return v == null || v === "" ? "-" : String(v);
}

export default function DueReportsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("overdue");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [stats, setStats] = useState({
    totalOverdue: 0,
    totalDueSoon: 0,
    averageDelay: 0,
    totalOrders: 0,
  });

  const [lastUpdated, setLastUpdated] = useState(null);
  const refreshInterval = useRef(null);

  const ITEMS_PER_PAGE = 10;

  // details modal
  const [openItem, setOpenItem] = useState(null);

  const computeLocalStats = (rows, totalsFromApi) => {
    let totalDelay = 0;
    let overdueCount = 0;

    rows.forEach((item) => {
      if (Number(item.minutesLeft) < 0) {
        overdueCount += 1;
        totalDelay += Math.abs(Number(item.minutesLeft));
      }
    });

    const avgDelay = overdueCount > 0 ? Math.round(totalDelay / overdueCount) : 0;
    const uniqueOrders = new Set(rows.map((x) => x.order?.id).filter(Boolean)).size;

    return {
      totalOverdue: totalsFromApi?.overdue ?? 0,
      totalDueSoon: totalsFromApi?.dueSoon ?? 0,
      averageDelay: avgDelay,
      totalOrders: uniqueOrders,
    };
  };

  async function loadData(tab = activeTab, page = currentPage) {
    setLoading(true);
    try {
      const url = `${BASE_URL}/api/orders/due?tab=${tab}&page=${page}&pageSize=${ITEMS_PER_PAGE}`;
      const res = await fetch(url, { credentials: "include" });
      const json = await res.json();

      setData(json);
      setLastUpdated(new Date());

      const rows = json?.data || [];
      const totals = json?.totals || {};
      const pagination = json?.pagination || {};

      setTotalPages(pagination.totalPages || 1);
      setStats(computeLocalStats(rows, totals));
    } catch (e) {
      console.error("Failed to load due reports:", e);
      setData(null);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData("overdue", 1);

    refreshInterval.current = setInterval(() => {
      loadData(activeTab, currentPage);
    }, 60000);

    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData(activeTab, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage]);

  const dashboardItems = data?.data || [];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));
  const handlePageChange = (page) => setCurrentPage(page);

  const renderPages = () => {
    if (totalPages <= 1) return null;

    const maxButtons = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);

    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);

    return (
      <div className="flex items-center gap-1">
        <button
          onClick={handlePrev}
          disabled={currentPage === 1}
          className={`px-2 py-1 text-xs rounded border ${
            currentPage === 1
              ? "text-gray-400 border-gray-200 bg-white"
              : "text-gray-700 border-gray-300 bg-white hover:bg-gray-100"
          }`}
        >
          Prev
        </button>

        {start > 1 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              className="px-2 py-1 text-xs rounded text-gray-600 hover:text-gray-800"
            >
              1
            </button>
            <span className="text-xs text-gray-500">…</span>
          </>
        )}

        {pages.map((p) => (
          <button
            key={p}
            onClick={() => handlePageChange(p)}
            className={`px-2 py-1 text-xs rounded border ${
              currentPage === p
                ? "bg-blue-100 text-blue-700 border-blue-200"
                : "text-gray-600 border-transparent hover:text-gray-800 hover:bg-gray-100"
            }`}
          >
            {p}
          </button>
        ))}

        {end < totalPages && (
          <>
            <span className="text-xs text-gray-500">…</span>
            <button
              onClick={() => handlePageChange(totalPages)}
              className="px-2 py-1 text-xs rounded text-gray-600 hover:text-gray-800"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className={`px-2 py-1 text-xs rounded border ${
            currentPage === totalPages
              ? "text-gray-400 border-gray-200 bg-white"
              : "text-gray-700 border-gray-300 bg-white hover:bg-gray-100"
          }`}
        >
          Next
        </button>
      </div>
    );
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const modalPayments = useMemo(() => {
    if (!openItem?.payments) return [];
    return Array.isArray(openItem.payments) ? openItem.payments : [];
  }, [openItem]);

  const sumPaid = useMemo(() => {
    return modalPayments.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
  }, [modalPayments]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} isMenuOpen={sidebarOpen} />
     <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-14 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4">
          <Breadcrumb className="mb-4" />

        <div>
                <h1 className="text-xl font-semibold text-gray-800">
                 Turn Around Time
                </h1>
                <p className="text-xs text-gray-500 mt-1">
                  Manage Turn Around Time
                </p>
              </div>

          <div className="">
            {/* Header */}
            <div className="border-b border-gray-200 px-4 py-3 bg-white rounded-t">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                
                  <p className="text-[11px] text-gray-500">
                    {lastUpdated ? `Last updated: ${lastUpdated.toLocaleString()}` : "—"}
                  </p>
                </div>

                <button
                  onClick={() => loadData(activeTab, currentPage)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">{stats.totalOverdue}</div>
                  <div className="text-xs text-gray-500">Overdue</div>
                </div>

                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">{stats.totalDueSoon}</div>
                  <div className="text-xs text-gray-500">Due Soon</div>
                </div>

                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">
                    {stats.averageDelay > 60 ? `${Math.floor(stats.averageDelay / 60)}h` : `${stats.averageDelay}m`}
                  </div>
                  <div className="text-xs text-gray-500">Avg Delay (page)</div>
                </div>

                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900">{stats.totalOrders}</div>
                  <div className="text-xs text-gray-500">Orders (page)</div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-4 py-2 border-b border-gray-200 bg-white">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleTabChange("overdue")}
                  className={`px-3 py-1 text-xs font-medium rounded-full ${
                    activeTab === "overdue"
                      ? "bg-red-100 text-red-700 border border-red-200"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  Overdue ({data?.totals?.overdue ?? 0})
                </button>

                <button
                  onClick={() => handleTabChange("dueSoon")}
                  className={`px-3 py-1 text-xs font-medium rounded-full ${
                    activeTab === "dueSoon"
                      ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  Due Soon ({data?.totals?.dueSoon ?? 0})
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto bg-white rounded-b shadow-sm">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">Patient</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">Order</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">Item</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">SLA Status</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">Due Time</th>

                    {/* ✅ NEW COLUMN */}
                    <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">
                      Dispatched (IST)
                    </th>

                    <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">Report Ready</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">Payments</th>
                    <th className="text-left py-2 px-4 font-medium text-gray-600 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="py-6 text-center">
                        <p className="text-xs text-gray-500">Loading...</p>
                      </td>
                    </tr>
                  ) : dashboardItems.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="py-6 text-center">
                        <p className="text-xs text-gray-500">
                          {activeTab === "overdue" ? "No overdue reports" : "No reports due soon"}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    dashboardItems.map((item) => {
                      const paymentCount = Array.isArray(item.payments) ? item.payments.length : 0;
                      const latestPayment = paymentCount ? item.payments[0] : null;

                      // ✅ dispatchedAt is UTC (DateTime in DB) -> convert to IST for display
                      const disp = utcToISTParts(item.dispatchedAt || item.dispatchedAtUTC); 
                      // NOTE: this expects API to send dispatchedAt in row.
                      // If your API currently sends dispatchedAt inside row as `dispatchedAt`, you are good.

                      return (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                          {/* Patient */}
                          <td className="py-2 px-4">
                            <div>
                              <div className="font-medium text-gray-900">{item.patient?.fullName || "-"}</div>
                              <div className="text-gray-500">{item.patient?.contactNo || "-"}</div>
                            </div>
                          </td>

                          {/* Order */}
                          <td className="py-2 px-4">
                            <div className="font-medium text-gray-900">{item.order?.id || item.order?.orderNumber || "-"}</div>
                            <div className="text-gray-500">
                              Created by: <span className="text-gray-700">{item.order?.createdBy?.name || "-"}</span>
                            </div>
                          </td>

                          {/* Item */}
                          <td className="py-2 px-4">
                            <div className="text-gray-900 font-medium">{item.item?.name || "-"}</div>
                            <div className="text-gray-500">{safeStr(item.itemType)}</div>
                          </td>

                          {/* SLA */}
                          <td className="py-2 px-4">
                            <div className={`font-medium ${Number(item.minutesLeft) < 0 ? "text-red-600" : "text-yellow-600"}`}>
                              {Number(item.minutesLeft) < 0
                                ? `Overdue ${formatMinutes(item.minutesLeft)}`
                                : `Due in ${formatMinutes(item.minutesLeft)}`}
                            </div>
                            <div className="text-gray-500">{safeStr(item.dispatchStatus)}</div>
                          </td>

                          {/* Due Time */}
                          <td className="py-2 px-4">
                            <div className="text-gray-700">{formatTimeFromISTString(item.reportDueAtIST)}</div>
                            <div className="text-gray-500">{formatDateFromISTString(item.reportDueAtIST)}</div>
                          </td>

                          {/* ✅ Dispatched (IST) */}
                          <td className="py-2 px-4">
                            <div className="text-gray-700">{disp.time}</div>
                            <div className="text-gray-500">{disp.date}</div>
                          </td>

                          {/* reportReady */}
                          <td className="py-2 px-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] ${
                                item.order?.reportReady
                                  ? "bg-green-50 text-green-700 border-green-200"
                                  : "bg-gray-50 text-gray-700 border-gray-200"
                              }`}
                            >
                              {item.order?.reportReady ? "YES" : "NO"}
                            </span>
                          </td>

                          {/* Payments */}
                          <td className="py-2 px-4">
                            <div className="text-gray-900 font-medium">{paymentCount ? `${paymentCount} payment(s)` : "—"}</div>
                            <div className="text-gray-500">
                              {latestPayment ? `Latest: ₹${inr(latestPayment.amount)} • ${safeStr(latestPayment.paymentStatus)}` : "No payments"}
                            </div>
                          </td>

                          {/* Action */}
                          <td className="py-2 px-4">
                            <button
                              onClick={() => setOpenItem(item)}
                              className="px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 text-xs"
                            >
                              View details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3 text-xs text-gray-600">
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                    <span>Overdue</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                    <span>Due Soon</span>
                  </div>
                  <div className="text-gray-500">
                    • Page {currentPage} of {totalPages}
                  </div>
                </div>

                {renderPages()}
              </div>
            </div>
          </div>
        </div>

        {/* DETAILS MODAL (kept same from your previous version) */}
        {openItem && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpenItem(null)} />
            <div className="absolute inset-0 flex items-center justify-center p-3">
              <div className="w-full max-w-4xl bg-white rounded-xl shadow-xl overflow-hidden">
                <div className="px-4 py-3 border-b flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Due Report Details</div>
                    <div className="text-[11px] text-gray-500">
                      {openItem.itemType} • {openItem.item?.name || "-"} • {openItem.dispatchStatus}
                    </div>
                  </div>
                  <button
                    onClick={() => setOpenItem(null)}
                    className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100"
                  >
                    Close
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  {/* Add dispatched time in modal too */}
                  <div className="rounded-lg border p-3">
                    <div className="text-[11px] text-gray-500 mb-2">Dispatch</div>
                    {(() => {
                      const d = utcToISTParts(openItem.dispatchedAt || openItem.dispatchedAtUTC);
                      return (
                        <div className="text-xs text-gray-700">
                          Dispatched (IST): <span className="font-medium">{d.full}</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Payments section (same as before) */}
                  <div className="rounded-lg border overflow-hidden">
                    <div className="px-3 py-2 bg-gray-50 border-b flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Payments</div>
                        <div className="text-[11px] text-gray-500">
                          Total: ₹{inr((openItem.payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0))} • Count:{" "}
                          {(openItem.payments || []).length}
                        </div>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-white">
                          <tr className="border-b">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Payment ID</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Amount</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Method</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Mode</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Created By</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {(openItem.payments || []).length === 0 ? (
                            <tr>
                              <td colSpan={7} className="py-4 text-center text-gray-500">
                                No payments found for this order.
                              </td>
                            </tr>
                          ) : (
                            (openItem.payments || []).map((p) => (
                              <tr key={p.id} className="hover:bg-gray-50">
                                <td className="py-2 px-3">
                                  <div className="font-medium text-gray-900">{safeStr(p.paymentId)}</div>
                                  <div className="text-gray-500 text-[11px]">Ref: {safeStr(p.referenceId)}</div>
                                </td>
                                <td className="py-2 px-3">
                                  <div className="font-semibold text-gray-900">₹{inr(p.amount)}</div>
                                  <div className="text-gray-500 text-[11px]">{safeStr(p.currency)}</div>
                                </td>
                                <td className="py-2 px-3">{safeStr(p.paymentMethod)}</td>
                                <td className="py-2 px-3">{safeStr(p.paymentMode)}</td>
                                <td className="py-2 px-3">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] ${badgeClass(p.paymentStatus)}`}>
                                    {safeStr(p.paymentStatus)}
                                  </span>
                                </td>
                                <td className="py-2 px-3">{p.paymentDate ? new Date(p.paymentDate).toLocaleString() : "-"}</td>
                                <td className="py-2 px-3">{p.createdBy?.name || "-"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setOpenItem(null)}
                    className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}